import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupCount, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  AvatarIniciales,
  AvisoNavy,
  DataTable,
  Icon,
  PageSection,
  ProgressBar,
  Tip,
  colorPorUmbral,
} from "@/components/ds";
import { ArrowLeft, AlertCircle, Loader2, Search } from "@/components/ds/icons";
import { capacitacionesIaApi } from "@/lib/api/capacitaciones-ia";
import { inicialesPersona } from "@/lib/iniciales";
import { cleanRut, formatRut } from "@/lib/rut";
import { CuestionarioCongelado } from "@/components/capacitaciones-ia/CuestionarioCongelado";
import { FuentesChips } from "@/components/capacitaciones-ia/FuentesChips";
import type {
  CapacitacionDetalle,
  Destinatario,
  DestinatarioStatus,
  ProgramaFuente,
} from "@/types/capacitaciones-ia";

/**
 * Sin tildes ni mayúsculas — mismo patrón usado en `Areas.tsx`/
 * `MaquinariaForm.tsx` (no está centralizado en `lib/`, se repite local en
 * cada pantalla que busca por texto).
 */
function normalizar(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

/**
 * Los 3 buckets de destinatario del ficha (WU6a/WU6b —
 * sdd/capacitaciones-pantalla-unica): "respondió" / "empezó sin terminar" /
 * "sin abrir". `abierto` y `con_errores` se pliegan juntos ("está en el
 * cuestionario pero todavía no lo cerró") — separarlos no le aporta nada a
 * quien mira la ficha, que sólo necesita saber a quién le falta terminar.
 *
 * El legado `expirado` (D7: sin plazo no hay "vencido" que distinguir) NUNCA
 * llega hasta acá — `CapacitacionDetalleResource` ya lo pliega en `pendiente`
 * en el propio backend — pero se mapea igual acá por si acaso, al mismo
 * bucket que `pendiente`, nunca a un cuarto bucket.
 */
type BucketDestinatario = "respondido" | "en_curso" | "sin_abrir";

function bucketDe(status: DestinatarioStatus): BucketDestinatario {
  if (status === "respondido") return "respondido";
  if (status === "abierto" || status === "con_errores") return "en_curso";
  return "sin_abrir";
}

/**
 * Semáforo de los 3 buckets. `texto` pinta ícono y número; `barra` pinta el
 * relleno. Son DOS tokens distintos a propósito: el `-bar` está calibrado
 * como fondo sólido y sobre blanco no llega al contraste que necesita un
 * número de 12px.
 *
 * Verde / ámbar / gris — el gris de "Sin abrir" NO es rojo: es el bucket
 * neutro, no una falla. Quien no abrió todavía puede abrir; pintarlo de rojo
 * lo trataría como un incumplimiento cerrado.
 */
const BUCKET_INFO: Record<
  BucketDestinatario,
  { label: string; tono: BadgeTone; icono: string; texto: string; barra: string }
> = {
  respondido: {
    label: "Respondió",
    tono: "success",
    icono: "task_alt",
    texto: "var(--success)",
    barra: "var(--success-bar)",
  },
  en_curso: {
    label: "Empezó sin terminar",
    tono: "warning",
    icono: "pending_actions",
    texto: "var(--warning)",
    barra: "var(--warning-bar)",
  },
  sin_abrir: {
    label: "Sin abrir",
    tono: "neutral",
    icono: "mail",
    texto: "var(--muted-foreground)",
    barra: "var(--border-strong)",
  },
};

/**
 * Fila de "Estado de los destinatarios" — a propósito NO es la `ProgressBar`
 * del DS. Esa componente colorea por umbral de CUMPLIMIENTO (más es mejor),
 * que tiene sentido para "Respondió" pero pintaría "Sin abrir" bajo como una
 * alerta cuando ahí bajo es justamente lo deseable — mismo criterio que
 * `BarraEnviadas` en `ProgramaCapacitaciones.tsx`.
 *
 * El color del bucket viaja por las TRES piezas de la fila (ícono, barra y
 * número) y no sólo por la barra: es lo que permite leer el semáforo de un
 * vistazo, sin recorrer las etiquetas.
 */
function FilaResultado({
  bucket,
  n,
  total,
}: {
  bucket: BucketDestinatario;
  n: number;
  total: number;
}) {
  const { label, icono, texto, barra } = BUCKET_INFO[bucket];
  const pct = total > 0 ? Math.round((n / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2.5">
      <Icon name={icono} size={16} className="shrink-0" style={{ color: texto }} />
      <span className="text-body flex-1 text-[12px]">{label}</span>
      <div className="bg-border-subtle h-[5px] w-[74px] shrink-0 overflow-hidden rounded-full">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: barra }} />
      </div>
      <span
        className="fc-num w-[22px] shrink-0 text-right text-[12.5px] font-medium"
        style={{ color: texto }}
      >
        {n}
      </span>
    </div>
  );
}

/**
 * Fila del panel "Cómo salió". El ícono va en gris decorativo a propósito:
 * acá el color no significa nada (no hay semáforo que leer), y teñirlo
 * competiría con el de "Estado de los destinatarios", que sí significa.
 */
function FilaMeta({
  icono,
  label,
  children,
  destacado = false,
}: {
  icono: string;
  label: string;
  children: React.ReactNode;
  destacado?: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon name={icono} size={16} className="text-icon-soft shrink-0" />
      <span className="text-soft w-[92px] shrink-0 text-[12px]">{label}</span>
      <span className={`min-w-0 flex-1 text-[12px] ${destacado ? "font-medium" : ""}`}>
        {children}
      </span>
    </div>
  );
}

/** `DD/MM/AAAA · HH:MM` en horario local, desde un ISO8601 con offset. */
function fechaHora(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()} · ${hh}:${mi}`;
}

export function FichaCapacitacion() {
  const [, params] = useRoute("/capacitaciones/programa/tematica/:tematicaId");
  const [, navigate] = useLocation();
  const id = params?.tematicaId ? Number(params.tematicaId) : null;

  const [detalle, setDetalle] = useState<CapacitacionDetalle | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const cargar = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setNotFound(false);
    try {
      const res = await capacitacionesIaApi.getDetalle(id);
      setDetalle(res);
    } catch {
      // 404 (temática de otra empresa, todavía no enviada, o inexistente)
      // cae en el mismo estado "no encontrada" — sin fuga de información
      // entre empresas, mismo criterio que `FichaVehiculo`.
      setNotFound(true);
      setDetalle(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const conteos = useMemo(() => {
    const base = { respondido: 0, en_curso: 0, sin_abrir: 0 };
    if (!detalle) return base;
    for (const d of detalle.destinatarios) base[bucketDe(d.status)]++;
    return base;
  }, [detalle]);

  // Filtro por bucket + búsqueda por nombre O rut (WU6b + WU6c). `rut` llega
  // canónico (sin puntos ni guión, `Rut::normalize()` en fc-api) — se compara
  // limpiando también lo que escribe el usuario con `cleanRut()`, así que da
  // igual si tipea con o sin formato.
  const [filtro, setFiltro] = useState<"todos" | BucketDestinatario>("todos");
  const [busca, setBusca] = useState("");

  const destinatariosVisibles = useMemo(() => {
    if (!detalle) return [];
    const q = normalizar(busca.trim());
    const qRut = cleanRut(busca);
    return detalle.destinatarios
      .filter((d) => filtro === "todos" || bucketDe(d.status) === filtro)
      .filter((d) => {
        if (!q) return true;
        if (normalizar(d.nombre ?? d.email).includes(q)) return true;
        return qRut.length > 0 && !!d.rut && cleanRut(d.rut).includes(qRut);
      });
  }, [detalle, filtro, busca]);

  if (!id) return null;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12 pt-6">
          <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (notFound || !detalle) {
    return (
      <div className="max-w-[1080px] space-y-4">
        <button
          type="button"
          onClick={() => navigate("/capacitaciones/programa")}
          className="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1.5 text-[12.5px] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver a Programas
        </button>
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <AlertCircle className="text-destructive h-6 w-6" />
            <p className="text-muted-foreground text-sm">
              No encontramos esta capacitación, o todavía no se envió.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { capacitacion, resumen, destinatarios } = detalle;

  // Fuentes del programa (CRITICAL-2, sdd/nuevo-programa-multifuente, verify
  // #1690): `fuentes` es el conjunto ORDENADO completo (documento_empresa +
  // adhoc, design #1663) — reemplaza al `documento` singular deprecado, que
  // queda `null` en un Programa sólo-ad-hoc. Antes de este fix esta pantalla
  // sólo miraba `documento`, así que un Programa sólo-ad-hoc mostraba un
  // hueco vacío donde debía ir la fuente. Mismo respaldo defensivo que
  // `ProgramaFilaExpandida.tsx`: `documento` sólo se usa si por algún motivo
  // `fuentes` llegara vacío (no debería pasar una vez F6 desplegado).
  const fuentes: ProgramaFuente[] =
    (capacitacion.programa?.fuentes?.length ?? 0) > 0
      ? (capacitacion.programa?.fuentes ?? [])
      : capacitacion.programa?.documento
        ? [
            {
              id: -1,
              tipo: "documento_empresa" as const,
              etiqueta:
                capacitacion.programa.documento.label ??
                capacitacion.programa.documento.filename_original ??
                "Documento",
              orden: 0,
              empresa_documento_id: capacitacion.programa.documento.id,
              filename_original: capacitacion.programa.documento.filename_original,
              size_bytes: null,
              descarga_url: "",
            },
          ]
        : [];
  const nPreguntas = (detalle.preguntas ?? []).length;
  // El porcentaje EXACTO que dibuja la `ProgressBar` de abajo, replicando su
  // redondeo. Ver el comentario en el número grande: es la única forma de que
  // número y barra caigan siempre del mismo lado del umbral.
  const pctBarra =
    resumen.total > 0 ? Math.min(100, Math.round((resumen.respondidos / resumen.total) * 100)) : 0;

  return (
    <div className="max-w-[1080px] space-y-4">
      <button
        type="button"
        onClick={() => navigate("/capacitaciones/programa")}
        className="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1.5 text-[12.5px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver a Programas
      </button>

      <div>
        <h1 className="text-[22px] leading-tight font-bold tracking-[-.025em]">{capacitacion.titulo}</h1>
        {capacitacion.objetivo && (
          <p className="text-soft mt-1 text-[13px] leading-[1.45]">{capacitacion.objetivo}</p>
        )}
      </div>

      <AvisoNavy
        icono="lock"
        titulo="Evidencia congelada"
        detalle="Esta capacitación ya salió a terreno: su contenido, su cuestionario y sus respuestas quedaron congelados como evidencia y no se editan. Si el contenido tenía un error, crea un programa nuevo con el material corregido."
      />

      <div className="bg-card flex flex-wrap items-center gap-4 rounded-xl border px-4 py-3.5">
        <div className="flex shrink-0 flex-col gap-0.5">
          <span className="text-muted-foreground text-[11.5px]">Respondieron</span>
          {/* Mismo semáforo que la barra de al lado, por el MISMO helper y
              sobre el MISMO porcentaje. `pctBarra` y no
              `resumen.porcentaje_respuesta`: el backend manda un decimal
              (80.4) y la barra redondea (80), así que en 201/250 el número
              saldría verde y la barra ámbar. */}
          <span
            className="fc-num text-[24px] leading-none font-semibold tracking-[-.025em]"
            style={{ color: colorPorUmbral(pctBarra, resumen.total <= 0) }}
          >
            {resumen.respondidos}/{resumen.total}
          </span>
        </div>
        <div className="min-w-[200px] flex-1">
          {/* `etiqueta` explícito: por defecto `ProgressBar` repite
              "valor/total", que acá ya se muestra arriba en grande — con el
              porcentaje la barra suma información en vez de repetirla. */}
          <ProgressBar
            valor={resumen.respondidos}
            total={resumen.total}
            etiqueta={`${resumen.porcentaje_respuesta}%`}
            alto={7}
          />
          <p className="text-soft mt-1.5 text-[11.5px] leading-[1.45]">
            {resumen.total > 0
              ? `${resumen.porcentaje_respuesta}% de los ${resumen.total} destinatarios ya respondió. El resto todavía no completó el cuestionario.`
              : "Esta capacitación no tiene destinatarios registrados."}
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {/* "Cómo salió" responde de dónde vino, cuándo salió y qué pesa —
            no repite el estado: la ficha entera SÓLO existe para temáticas ya
            enviadas, y el aviso de evidencia congelada arriba ya lo dice. */}
        <PageSection titulo="Cómo salió">
          <div className="flex flex-col gap-2.5 p-4">
            <FilaMeta icono="school" label="Programa" destacado>
              <span className="fc-truncate block">
                {capacitacion.programa?.nombre ?? "Programa sin nombre"}
              </span>
            </FilaMeta>
            {/* Reusa `FuentesChips` (W3, sdd/nuevo-programa-multifuente) en vez
                de una tercera repetición del texto "label · filename" —
                muestra TODAS las fuentes (no sólo la primaria) y ya trae la
                descarga para un ad-hoc. No puede ir dentro de `FilaMeta`: sus
                `children` renderizan en un `<span>`, y `FuentesChips` es un
                `<div>` (chips con `flex-wrap`). */}
            {fuentes.length > 0 && (
              <div className="flex items-start gap-2.5">
                <Icon name="description" size={16} className="text-icon-soft mt-0.5 shrink-0" />
                <span className="text-soft w-[92px] shrink-0 text-[12px]">
                  {fuentes.length === 1 ? "Fuente" : "Fuentes"}
                </span>
                <FuentesChips fuentes={fuentes} />
              </div>
            )}
            <FilaMeta icono="send" label="Enviada">
              {capacitacion.enviado_at ? fechaHora(capacitacion.enviado_at) : "Sin registro"}
            </FilaMeta>
            {/* Duración y nº de preguntas van juntos: los dos responden
                "cuánto le va a tomar esto al trabajador". `duracion_min`
                puede no haberse declarado — ahí queda sólo el cuestionario,
                nunca un "0 min" inventado.
                `?? []`: defensivo contra un backend que todavía no tenga el
                campo `preguntas` (WU3) desplegado — sin esto, la ficha
                entera se rompe en blanco (confirmado en verificación CDP). */}
            <FilaMeta icono="schedule" label="Duración">
              {capacitacion.duracion_min
                ? `${capacitacion.duracion_min} min estimados · ${nPreguntas} ${nPreguntas === 1 ? "pregunta" : "preguntas"}`
                : `${nPreguntas} ${nPreguntas === 1 ? "pregunta" : "preguntas"}`}
            </FilaMeta>
            <FilaMeta icono="groups" label="Destinatarios">
              {resumen.total} {resumen.total === 1 ? "trabajador" : "trabajadores"}
            </FilaMeta>
          </div>
        </PageSection>

        <PageSection titulo="Estado de los destinatarios">
          <div className="flex flex-col gap-2.5 p-4">
            <FilaResultado bucket="respondido" n={conteos.respondido} total={resumen.total} />
            <FilaResultado bucket="en_curso" n={conteos.en_curso} total={resumen.total} />
            <FilaResultado bucket="sin_abrir" n={conteos.sin_abrir} total={resumen.total} />
          </div>
        </PageSection>
      </div>

      <PageSection
        titulo="Destinatarios"
        descripcion={`${resumen.total} ${resumen.total === 1 ? "trabajador recibió" : "trabajadores recibieron"} esta capacitación`}
      >
        <DataTable<Destinatario>
          rows={destinatariosVisibles}
          getRowKey={(d, i) => `${d.email}-${i}`}
          minWidth={520}
          pageSize={false}
          emptyMessage={
            busca.trim()
              ? `Ningún destinatario coincide con "${busca.trim()}".`
              : "Ningún destinatario en este estado."
          }
          emptyIcon="search_off"
          toolbar={
            <>
              <ToggleGroup
                type="single"
                variant="segmented"
                value={filtro}
                onValueChange={(v) => v && setFiltro(v as typeof filtro)}
              >
                <ToggleGroupItem value="todos">
                  Todos
                  <ToggleGroupCount>{destinatarios.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="respondido">
                  Respondió
                  <ToggleGroupCount>{conteos.respondido}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="en_curso">
                  Empezó sin terminar
                  <ToggleGroupCount>{conteos.en_curso}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="sin_abrir">
                  Sin abrir
                  <ToggleGroupCount>{conteos.sin_abrir}</ToggleGroupCount>
                </ToggleGroupItem>
              </ToggleGroup>
              <div className="relative min-w-[190px] flex-1">
                <Search className="text-soft absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder="Buscar por nombre o RUT…"
                  className="h-9 pl-8 text-[13px]"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                />
              </div>
            </>
          }
          columns={[
            {
              key: "trabajador",
              header: "Trabajador",
              width: "minmax(200px,1.4fr)",
              cell: (d) => (
                <div className="flex min-w-0 items-center gap-2.5 pr-3">
                  <AvatarIniciales iniciales={inicialesPersona(d.nombre)} tamano="fila" tono="teal" />
                  <div className="flex min-w-0 flex-col gap-px">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span className="fc-cell fc-truncate">{d.nombre ?? "Sin nombre"}</span>
                      {/* D10 (sdd/capacitaciones-pantalla-unica): el denominador se
                          congela al enviar — esta bandera sólo EXPLICA inline por
                          qué alguien sigue contando aunque ya no esté activo, nunca
                          lo saca de la cuenta. */}
                      {d.persona_activo === false && (
                        <Tip texto="Esta persona fue desactivada después de que se le envió esta capacitación. Sigue contando en el total: la evidencia no se mueve.">
                          <Badge variant="muted" className="shrink-0 gap-1">
                            <Icon name="person_off" size={12} />
                            Inactivo
                          </Badge>
                        </Tip>
                      )}
                    </div>
                    <span className="fc-truncate text-soft text-[10.5px]">
                      {d.email}
                      {d.rut && <span className="fc-num">{" · "}{formatRut(d.rut)}</span>}
                    </span>
                  </div>
                </div>
              ),
            },
            {
              key: "estado",
              header: "Estado",
              width: "170px",
              // Ícono en vez de punto: el glifo distingue los 3 estados por
              // FORMA además de por color, así la fila sigue siendo legible
              // para quien no separa el verde del ámbar. El color lo pone el
              // tono del badge — el mismo de los 3 buckets de arriba.
              cell: (d) => {
                const info = BUCKET_INFO[bucketDe(d.status)];
                return (
                  <Badge variant={info.tono} className="gap-1">
                    <Icon name={info.icono} size={13} />
                    {info.label}
                  </Badge>
                );
              },
            },
          ]}
        />
      </PageSection>

      <CuestionarioCongelado preguntas={detalle.preguntas ?? []} pdfUrl={capacitacion.pdf_url} />
    </div>
  );
}
